import { addDateOnlyDays, type DateOnly } from "./date-only";
import {
  LEAVE_TYPE_LABELS,
  MAIN_TEAMS,
  type LeaveDuration,
  type LeaveType,
  type MainTeam,
  type SchedulePattern,
  type Shift,
} from "./domain-types";
import { getMainTeamShift, getShiftForPattern, getTeamsForShift } from "./shifts";

export interface TeamChief {
  loginCode: string;
  name: string;
  team: MainTeam;
  schedulePattern: SchedulePattern;
}

export const TEAM_CHIEFS: Readonly<Record<MainTeam, TeamChief>> = {
  A: { loginCode: "77784", name: "Gökhan Göksel Aytar", team: "A", schedulePattern: "A2" },
  B: { loginCode: "76995", name: "Eray Karakuş", team: "B", schedulePattern: "B1" },
  C: { loginCode: "58444", name: "Burhan Şahin", team: "C", schedulePattern: "C3" },
  D: { loginCode: "76312", name: "Ümit Ozan Çelik", team: "D", schedulePattern: "D1" },
};

export interface ChiefAbsence {
  date: DateOnly;
  leaveType: LeaveType;
  duration: LeaveDuration;
}

export interface ChiefShiftChange {
  date: DateOnly;
  newShift: Exclude<Shift, "OFF">;
}

export interface ChiefRuntimeState {
  active?: boolean;
  approvedAbsences?: readonly ChiefAbsence[];
  approvedShiftChanges?: readonly ChiefShiftChange[];
  approvedDuties?: ReadonlyArray<{ date: DateOnly; location: string }>;
}

export type ChiefAvailabilityCode =
  | "AVAILABLE"
  | "INACTIVE"
  | "FULL_ABSENCE"
  | "OFF"
  | "SHIFT_MISMATCH"
  | "PARTIAL_ABSENCE"
  | "ON_DUTY";

export interface ChiefAvailability {
  kind: "AVAILABLE" | "UNAVAILABLE" | "PARTIAL";
  code: ChiefAvailabilityCode;
  chief: TeamChief;
  date: DateOnly;
  requiredShift: Shift;
  effectiveShift: Shift;
  warning: string | null;
}

export function getConsecutiveOffDays(pattern: SchedulePattern, date: DateOnly): number {
  if (getShiftForPattern(pattern, date) !== "OFF") return 0;
  let count = 1;
  for (let offset = 1; offset < 32 && getShiftForPattern(pattern, addDateOnlyDays(date, -offset)) === "OFF"; offset += 1) {
    count += 1;
  }
  for (let offset = 1; offset < 32 && getShiftForPattern(pattern, addDateOnlyDays(date, offset)) === "OFF"; offset += 1) {
    count += 1;
  }
  return Math.min(count, 32);
}

export function getChiefAvailability(
  chief: TeamChief,
  date: DateOnly,
  requiredShift: Shift,
  state: ChiefRuntimeState = {},
): ChiefAvailability {
  const baseShift = getShiftForPattern(chief.schedulePattern, date);
  const approvedChange = state.approvedShiftChanges?.find((change) => change.date === date);
  const effectiveShift = approvedChange?.newShift ?? baseShift;
  const fullAbsence = state.approvedAbsences?.find(
    (absence) => absence.date === date && absence.duration === "FULL_DAY",
  );
  const partialAbsence = state.approvedAbsences?.find(
    (absence) => absence.date === date && absence.duration !== "FULL_DAY",
  );
  const duty = state.approvedDuties?.find((item) => item.date === date);

  if (state.active === false) {
    return {
      kind: "UNAVAILABLE",
      code: "INACTIVE",
      chief,
      date,
      requiredShift,
      effectiveShift,
      warning: `${chief.name} hesabı pasif — ${chief.team} ekibinden başka bir personelin iş devri yapması gerekebilir.`,
    };
  }

  if (duty) {
    return {
      kind: "UNAVAILABLE",
      code: "ON_DUTY",
      chief,
      date,
      requiredShift,
      effectiveShift,
      warning: `${chief.name} görevde (${duty.location}) — ${chief.team} ekibinden başka bir personelin iş devri yapması gerekebilir.`,
    };
  }

  if (fullAbsence) {
    const leaveLabel = LEAVE_TYPE_LABELS[fullAbsence.leaveType].toLocaleLowerCase("tr-TR");
    return {
      kind: "UNAVAILABLE",
      code: "FULL_ABSENCE",
      chief,
      date,
      requiredShift,
      effectiveShift,
      warning: `${chief.name} ${leaveLabel}li — ${chief.team} ekibinden başka bir personelin iş devri yapması gerekebilir.`,
    };
  }

  if (effectiveShift === "OFF") {
    const offDays = getConsecutiveOffDays(chief.schedulePattern, date);
    const warning = offDays >= 3
      ? `${chief.name} ${offDays} günlük OFF döneminde. İş devri ekipten başka bir personel tarafından yapılabilir.`
      : `${chief.name} OFF — ${chief.team} ekibinden başka bir personelin iş devri yapması gerekebilir.`;
    return {
      kind: "UNAVAILABLE",
      code: "OFF",
      chief,
      date,
      requiredShift,
      effectiveShift,
      warning,
    };
  }

  if (effectiveShift !== requiredShift) {
    return {
      kind: "UNAVAILABLE",
      code: "SHIFT_MISMATCH",
      chief,
      date,
      requiredShift,
      effectiveShift,
      warning: `${chief.name} gerekli vardiyada değil — ${chief.team} ekibinden başka bir personelin iş devri yapması gerekebilir.`,
    };
  }

  if (partialAbsence) {
    return {
      kind: "PARTIAL",
      code: "PARTIAL_ABSENCE",
      chief,
      date,
      requiredShift,
      effectiveShift,
      warning: `${chief.name}: Kısmi izin bulunuyor, iş devri teyit edilmeli.`,
    };
  }

  return {
    kind: "AVAILABLE",
    code: "AVAILABLE",
    chief,
    date,
    requiredShift,
    effectiveShift,
    warning: null,
  };
}

export interface HandoffParticipant {
  team: MainTeam;
  chief: TeamChief;
  label: string;
  availability: ChiefAvailability;
}

export interface EstimatedHandoff {
  title: "Tahmini iş devri";
  date: DateOnly;
  nightTeam: MainTeam;
  morningTeam: MainTeam;
  fromShift: Exclude<Shift, "OFF">;
  toShift: Exclude<Shift, "OFF">;
  from: HandoffParticipant;
  to: HandoffParticipant;
  summary: string;
  warnings: string[];
}

export interface EstimateHandoffOptions {
  chiefs?: Readonly<Record<MainTeam, TeamChief>>;
  chiefStates?: Partial<Readonly<Record<MainTeam, ChiefRuntimeState>>>;
}

function handoffParticipant(
  team: MainTeam,
  date: DateOnly,
  requiredShift: Shift,
  chief: TeamChief,
  state: ChiefRuntimeState | undefined,
): HandoffParticipant {
  const availability = getChiefAvailability(chief, date, requiredShift, state);
  return {
    team,
    chief,
    label: availability.kind === "UNAVAILABLE" ? `${team} ekibinden başka personel` : chief.name,
    availability,
  };
}

function estimateHandoffRoute(
  date: DateOnly,
  fromShift: Exclude<Shift, "OFF">,
  toShift: Exclude<Shift, "OFF">,
  options: EstimateHandoffOptions = {},
): EstimatedHandoff {
  // Shift dates refer to the day the shift starts; 07:00 receives the prior night's work.
  const fromDate = fromShift === "GECE" ? addDateOnlyDays(date, -1) : date;
  const nightTeam = getTeamsForShift(fromDate, fromShift)[0];
  const morningTeam = getTeamsForShift(date, toShift)[0];
  if (!nightTeam || !morningTeam) {
    throw new RangeError("Tahmini iş devri ekipleri hesaplanamadı.");
  }

  const chiefs = options.chiefs ?? TEAM_CHIEFS;
  const from = handoffParticipant(
    nightTeam,
    fromDate,
    getMainTeamShift(nightTeam, fromDate),
    chiefs[nightTeam],
    options.chiefStates?.[nightTeam],
  );
  const to = handoffParticipant(
    morningTeam,
    date,
    getMainTeamShift(morningTeam, date),
    chiefs[morningTeam],
    options.chiefStates?.[morningTeam],
  );

  return {
    title: "Tahmini iş devri",
    date,
    nightTeam,
    morningTeam,
    fromShift,
    toShift,
    from,
    to,
    summary: `${from.label} → ${to.label}`,
    warnings: [from.availability.warning, to.availability.warning].filter(
      (warning): warning is string => warning !== null,
    ),
  };
}

export function estimateHandoffs(
  date: DateOnly,
  options: EstimateHandoffOptions = {},
): EstimatedHandoff[] {
  return [
    estimateHandoffRoute(date, "GECE", "SABAH", options),
    estimateHandoffRoute(date, "SABAH", "OGLE", options),
    estimateHandoffRoute(date, "OGLE", "GECE", options),
  ];
}

export function estimateHandoff(
  date: DateOnly,
  options: EstimateHandoffOptions = {},
): EstimatedHandoff {
  return estimateHandoffRoute(date, "GECE", "SABAH", options);
}

export const getEstimatedHandoff = estimateHandoff;

export function getSevenDayHandoffForecast(
  startDate: DateOnly,
  options: EstimateHandoffOptions = {},
): EstimatedHandoff[] {
  return Array.from({ length: 7 }, (_, index) => estimateHandoff(addDateOnlyDays(startDate, index), options));
}

/** Verifies the main-team constants are exhaustive at runtime when ingesting external data. */
export function hasAllTeamChiefs(chiefs: Readonly<Partial<Record<MainTeam, TeamChief>>>): boolean {
  return MAIN_TEAMS.every((team) => chiefs[team] !== undefined);
}
